import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import type { ComponentPageData, RawQuestionSettings } from '@/features/content/useComponent';

import { useUpdateComponentSettings } from './hooks/useUpdateComponentSettings';
import styles from './AssignQuestionsDialog.module.css';

interface AssignQuestionsDialogProps {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
}

export function AssignQuestionsDialog({
  component,
  componentId,
  courseId,
  onOpenChange,
  open,
}: AssignQuestionsDialogProps) {
  const mutation = useUpdateComponentSettings();
  const [assignmentMode, setAssignmentMode] = useState<'ALL' | 'USER_DEFINED'>('ALL');
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const isSubset =
      component.controlPanel.homeworkAssignedIndexes.length !==
      component.controlPanel.homeworkQuestionOptions.length;

    setAssignmentMode(isSubset ? 'USER_DEFINED' : 'ALL');
    setSelectedIndexes(component.controlPanel.homeworkAssignedIndexes);
  }, [component, open]);

  async function handleSave() {
    await mutation.mutateAsync({
      applyChanges: (draft) => {
        const nextSettings: RawQuestionSettings = {
          ...((draft.question_settings ??
            draft.questionSettings ??
            draft.course_question_settings ??
            draft.courseQuestionSettings ??
            {}) as RawQuestionSettings),
          type: assignmentMode === 'ALL' ? 1 : 4,
          user_defined_numbers: assignmentMode === 'ALL' ? [] : selectedIndexes,
        };

        draft.question_settings = nextSettings;
        draft.questionSettings = nextSettings;
      },
      component,
      componentId,
      courseId,
    });

    onOpenChange(false);
  }

  const questionOptions = component.controlPanel.homeworkQuestionOptions;
  const allIndexes = questionOptions
    .map((question) => question.index)
    .sort((left, right) => left - right);
  const allSelected =
    questionOptions.length > 0 && selectedIndexes.length === questionOptions.length;
  const noneSelected = selectedIndexes.length === 0;

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>Assign Questions</Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.description}>
                Choose whether every homework question is assigned or whether the
                section uses a manually selected subset.
              </p>
            </Dialog.Description>
          </div>

          <div className={styles.body}>
            <fieldset className={styles.fieldset}>
              <legend className={styles.legend}>Assignment mode</legend>
              <label className={styles.radioLabel}>
                <input
                  checked={assignmentMode === 'ALL'}
                  onChange={() => setAssignmentMode('ALL')}
                  type="radio"
                />
                <span>Assign all questions</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  checked={assignmentMode === 'USER_DEFINED'}
                  onChange={() => setAssignmentMode('USER_DEFINED')}
                  type="radio"
                />
                <span>Assign a subset of the questions</span>
              </label>
            </fieldset>

            {assignmentMode === 'USER_DEFINED' ? (
              <div className={styles.selector}>
                <div className={styles.selectorToolbar}>
                  <p aria-live="polite" className={styles.selectionCount}>
                    {selectedIndexes.length} of {questionOptions.length} selected
                  </p>
                  <div className={styles.selectorActions}>
                    <Button
                      disabled={allSelected}
                      onClick={() => setSelectedIndexes(allIndexes)}
                      size="small"
                      type="button"
                      variant="secondary"
                    >
                      Select all
                    </Button>
                    <Button
                      disabled={noneSelected}
                      onClick={() => setSelectedIndexes([])}
                      size="small"
                      type="button"
                      variant="secondary"
                    >
                      Deselect all
                    </Button>
                  </div>
                </div>
                <div className={styles.selectorGrid}>
                  {questionOptions.map((question) => (
                    <label className={styles.questionCard} key={question.id}>
                      <input
                        checked={selectedIndexes.includes(question.index)}
                        onChange={(event) => {
                          setSelectedIndexes((current) =>
                            event.target.checked
                              ? [...current, question.index].sort(
                                  (left, right) => left - right,
                                )
                              : current.filter((index) => index !== question.index),
                          );
                        }}
                        type="checkbox"
                      />
                      <span className={styles.questionLabel}>
                        Question {question.index + 1}
                      </span>
                      <span className={styles.questionPreview}>
                        {question.previewText}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {mutation.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {mutation.error.message}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button className={styles.cancelButton} variant="secondary">
                CANCEL
              </Button>
            </Dialog.Close>
            <Button
              className={styles.saveButton}
              disabled={mutation.isPending}
              onClick={() => {
                void handleSave();
              }}
              variant="primary"
            >
              {mutation.isPending ? 'Saving…' : 'SAVE'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
